import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaUsers, FaFilter, FaSpinner } from 'react-icons/fa';
import { segmentAPI } from './campaignService';

const styles = {
    container: {
        padding: '30px',
        color: '#FFFFFF'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
    },
    title: {
        fontSize: '20px',
        fontWeight: '600',
        margin: 0
    },
    closeBtn: {
        background: 'transparent',
        border: 'none',
        color: '#94A3B8',
        fontSize: '20px',
        cursor: 'pointer'
    },
    section: {
        marginBottom: '24px'
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.7)',
        marginBottom: '12px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        background: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        color: '#FFFFFF',
        fontSize: '14px',
        boxSizing: 'border-box'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        background: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        color: '#FFFFFF',
        fontSize: '14px',
        minHeight: '80px',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box'
    },
    label: {
        display: 'block',
        fontSize: '13px',
        color: 'rgba(255, 255, 255, 0.6)',
        marginBottom: '8px'
    },
    filterCard: {
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid rgba(255, 255, 255, 0.05)'
    },
    filterTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    checkboxGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px'
    },
    checkbox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        background: 'rgba(100, 116, 139, 0.2)',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '13px',
        color: '#94A3B8',
        transition: 'all 0.2s ease',
        border: '1px solid transparent'
    },
    checkboxActive: {
        background: 'rgba(139, 92, 246, 0.2)',
        color: '#8b5cf6',
        borderColor: 'rgba(139, 92, 246, 0.3)'
    },
    previewCard: {
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        marginTop: '24px',
        border: '1px solid rgba(139, 92, 246, 0.3)'
    },
    previewCount: {
        fontSize: '48px',
        fontWeight: '700',
        color: '#8b5cf6',
        marginBottom: '8px'
    },
    previewLabel: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)'
    },
    sampleList: {
        marginTop: '16px',
        textAlign: 'left'
    },
    sampleItem: {
        padding: '8px 12px',
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '6px',
        marginBottom: '6px',
        fontSize: '13px',
        color: 'rgba(255, 255, 255, 0.8)'
    },
    actions: {
        display: 'flex',
        gap: '12px',
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
    },
    btn: {
        padding: '12px 24px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        justifyContent: 'center'
    },
    primaryBtn: {
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        color: '#FFFFFF'
    },
    secondaryBtn: {
        background: 'rgba(100, 116, 139, 0.2)',
        border: '1px solid rgba(100, 116, 139, 0.4)',
        color: '#94A3B8'
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
    }
};

const FUNNEL_STATUSES = [
    { value: 'opened', label: 'Opened Widget', description: 'Users who opened the widget' },
    { value: 'started', label: 'Started Conversation', description: 'Started chatting' },
    { value: 'finished', label: 'Finished Conversation', description: 'Completed the chat' },
    { value: 'linked', label: 'Signed Up', description: 'Created an account' },
    { value: 'meeting_created', label: 'Meeting Scheduled', description: 'Booked a meeting' },
    { value: 'concluded', label: 'Process Complete', description: 'Finished advisory process' }
];

const SegmentBuilder = ({ expertId, segment, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [filters, setFilters] = useState({
        funnelStatus: [],
        inactiveDays: null,
        hasEmail: true
    });
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (segment) {
            setName(segment.name || '');
            setDescription(segment.description || '');
            setFilters(segment.filters || { funnelStatus: [], hasEmail: true });
        }
    }, [segment]);

    const toggleFunnelStatus = (status) => {
        setFilters(prev => {
            const current = prev.funnelStatus || [];
            const newStatuses = current.includes(status)
                ? current.filter(s => s !== status)
                : [...current, status];
            return { ...prev, funnelStatus: newStatuses };
        });
    };

    const handlePreview = async () => {
        setLoading(true);
        try {
            const result = await segmentAPI.previewSegment(expertId, filters);
            setPreview(result);
        } catch (error) {
            console.error('Error previewing segment:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            alert('Please enter a name for your segment');
            return;
        }

        setSaving(true);
        try {
            const segmentData = {
                name,
                description,
                filters,
                estimatedCount: preview?.totalCount
            };

            if (segment?._id) {
                await segmentAPI.updateSegment(expertId, segment._id, segmentData);
            } else {
                await segmentAPI.createSegment(expertId, segmentData);
            }

            onSave();
        } catch (error) {
            console.error('Error saving segment:', error);
            alert('Failed to save segment');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>
                    <FaUsers style={{ marginRight: '12px', color: '#8b5cf6' }} />
                    {segment ? 'Edit Segment' : 'Create Segment'}
                </h2>
                <button style={styles.closeBtn} onClick={onClose}>
                    <FaTimes />
                </button>
            </div>

            {/* Basic Info */}
            <div style={styles.section}>
                <label style={styles.label}>Segment Name *</label>
                <input 
                    type="text"
                    style={styles.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Inactive Users, Pending Sign-ups"
                />
            </div>

            <div style={styles.section}>
                <label style={styles.label}>Description</label>
                <textarea 
                    style={styles.textarea}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description of this segment"
                />
            </div>

            {/* Funnel Status Filter */}
            <div style={styles.filterCard}>
                <div style={styles.filterTitle}>
                    <FaFilter /> Filter by Status
                </div>
                <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '16px' }}>
                    Select which user statuses to include
                </p>
                <div style={styles.checkboxGroup}>
                    {FUNNEL_STATUSES.map(status => (
                        <div
                            key={status.value}
                            style={{
                                ...styles.checkbox,
                                ...(filters.funnelStatus?.includes(status.value) ? styles.checkboxActive : {})
                            }}
                            onClick={() => toggleFunnelStatus(status.value)}
                        >
                            <input 
                                type="checkbox"
                                checked={filters.funnelStatus?.includes(status.value)}
                                onChange={() => {}}
                                style={{ cursor: 'pointer' }}
                            />
                            {status.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Inactivity Filter */}
            <div style={styles.filterCard}>
                <div style={styles.filterTitle}>
                    <FaFilter /> Filter by Inactivity
                </div>
                <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '16px' }}>
                    Only include users who haven't been active for a specific number of days
                </p>
                <div style={styles.row}>
                    <div>
                        <label style={styles.label}>Inactive for (days)</label>
                        <input 
                            type="number"
                            style={styles.input}
                            value={filters.inactiveDays || ''}
                            onChange={(e) => setFilters({ 
                                ...filters, 
                                inactiveDays: e.target.value ? parseInt(e.target.value) : null 
                            })}
                            placeholder="e.g., 30"
                            min="1"
                        />
                    </div>
                </div>
            </div>

            {/* Preview Button */}
            <button 
                style={{ 
                    ...styles.btn, 
                    ...styles.secondaryBtn, 
                    width: '100%',
                    marginTop: '16px'
                }}
                onClick={handlePreview}
                disabled={loading}
            >
                {loading ? <FaSpinner className="spin" /> : <FaUsers />}
                Preview Recipients
            </button>

            {/* Preview Results */}
            {preview && (
                <div style={styles.previewCard}>
                    <div style={styles.previewCount}>{preview.totalCount}</div>
                    <div style={styles.previewLabel}>Matching Recipients</div>
                    
                    {preview.sampleRecipients && preview.sampleRecipients.length > 0 && (
                        <div style={styles.sampleList}>
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '8px' }}>
                                Sample recipients:
                            </div>
                            {preview.sampleRecipients.slice(0, 5).map((r, i) => (
                                <div key={i} style={styles.sampleItem}>
                                    {r.name || 'Unknown'} - {r.email}
                                    <span style={{ marginLeft: '8px', opacity: 0.6 }}>({r.status})</span>
                                </div>
                            ))}
                            {preview.sampleRecipients.length > 5 && (
                                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '8px' }}>
                                    ...and {preview.totalCount - 5} more
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div style={styles.actions}>
                <button 
                    style={{ ...styles.btn, ...styles.secondaryBtn }}
                    onClick={onClose}
                >
                    Cancel
                </button>
                <button 
                    style={{ ...styles.btn, ...styles.primaryBtn }}
                    onClick={handleSave}
                    disabled={saving}
                >
                    <FaSave /> {saving ? 'Saving...' : 'Save Segment'}
                </button>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin {
                    animation: spin 1s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default SegmentBuilder;
